import { useEffect, useMemo, useState } from 'react';
import Widget from '../../../components/Dashboard/Widget';
import Table from 'components/Table';
import { ColumnSetting } from 'components/Table/utils';
import useUsers from 'contexts/basicData/useUsers';
import Checkbox from 'components/inputs/Checkbox';
import CaseOpenLink from 'components/CaseOpenLink';
import { useDashboardControls } from 'contexts/Dashboard/useDashboardControls';
import { useCaseMarkEventsAsReadByAdmin } from 'api/case/case';
import { useQueryClient } from '@tanstack/react-query';
import { CaseEventDTO, TranslationLookups } from 'api/model';
import { LongTitles, TLWidgetsEnum, UPDATE_INTERVAL } from '../utils';
import {
  Centered,
  getTableRowClassName,
  SavingRowAnimationWrapper,
} from 'components/Dashboard/table-helpers';
import useTranslations from 'contexts/basicData/useTranslations';
import { makeStringComparator } from 'utils/sorting';
import {
  getTransportLogisticsGetUnhandledNoTyresLoadedEventsQueryKey,
  useTransportLogisticsGetUnhandledNoTyresLoadedEvents,
} from 'api/transport-logistics/transport-logistics';
import useBaseCaseEventWidgetColumnSettings from './useBaseCaseEventWidgetColumnSettings';

const TABLE_NAME = 'work-gear-reports';

const NotLoadedTyresWidget = () => {
  const [data, setData] = useState<CaseEventDTO[]>([]);
  const [ongoingSaves, setOngoingSaves] = useState<number[]>([]);
  const users = useUsers();
  const {
    dashboardState: { daterange, selectedBusinessUnits, searchText },
  } = useDashboardControls();
  const translations = useTranslations();

  const queryClient = useQueryClient();
  const { mutateAsync: markEventAsRead, status: markAsReadStatus } =
    useCaseMarkEventsAsReadByAdmin({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries([
            getTransportLogisticsGetUnhandledNoTyresLoadedEventsQueryKey(),
          ]);
        },
      },
    });

  const getNotLoadedTyres =
    useTransportLogisticsGetUnhandledNoTyresLoadedEvents(
      {
        startDate: daterange[0] ?? undefined,
        endDate: daterange[1] ?? undefined,
        businessUnitIDs: selectedBusinessUnits,
      },
      {
        query: {
          refetchInterval: UPDATE_INTERVAL,
        },
      }
    );

  useEffect(() => {
    if (getNotLoadedTyres.data) {
      setData(getNotLoadedTyres.data);
    } else {
      setData([] as CaseEventDTO[]);
    }
  }, [getNotLoadedTyres.data]);

  const filteredGearReports = useMemo(() => {
    let filtered = [...data];

    if (searchText.length > 0) {
      filtered = filtered.length
        ? filtered
            .filter((c) => {
              return (
                c.comment?.toLowerCase().includes(searchText.toLowerCase()) ||
                c.registrationNumber
                  .toLowerCase()
                  .includes(searchText.toLowerCase()) ||
                (users[c.createdByID]?.name ?? '')
                  .toLowerCase()
                  .includes(searchText.toLowerCase())
              );
            })
            .sort((a, b) => b.created.getTime() - a.created.getTime()) // Latest first
        : [];
    }

    return filtered;
  }, [data, searchText, users]);

  const baseColumnSettings = useBaseCaseEventWidgetColumnSettings();

  const columnSettings: ColumnSetting<CaseEventDTO, TranslationLookups>[] =
    useMemo(() => {
      const columns: ColumnSetting<CaseEventDTO, TranslationLookups>[] =
        baseColumnSettings.concat([
          {
            head: 'Rapport',
            render: (row) => row.comment,
            width: 300,
            sortFunction: makeStringComparator(
              (row) => row.data ?? row.comment
            ),
          },
          {
            head: ' ',
            render: (row) => (
              <Centered>
                <CaseOpenLink caseID={row.caseID} />
              </Centered>
            ),
            width: 20,
          },
          {
            head: 'Klart',
            render: (row) => (
              <Centered>
                <Checkbox
                  disabled={markAsReadStatus === 'loading'}
                  onChange={async () => {
                    setOngoingSaves([...ongoingSaves, row.id]);
                    await markEventAsRead({ data: [row.id] });
                    setOngoingSaves(ongoingSaves.filter((id) => id !== row.id));
                  }}
                />
              </Centered>
            ),
            width: 30,
          },
        ]);
      return columns;
    }, [baseColumnSettings, markAsReadStatus, markEventAsRead, ongoingSaves]);
  return (
    <Widget
      title={LongTitles[TLWidgetsEnum.NotLoadedTyres]}
      count={data.length}
      isLoading={getNotLoadedTyres.isLoading}
      info="Markera som klar för att markera utrustningsrapport som läst."
    >
      <SavingRowAnimationWrapper
        tableName={TABLE_NAME}
        savingIds={ongoingSaves}
      >
        <Table
          columnSettings={columnSettings}
          rows={filteredGearReports}
          rowClassName={(row) => getTableRowClassName(TABLE_NAME, row.id)}
          useColumnWidthAsFlexAmount
          renderProps={translations}
        />
      </SavingRowAnimationWrapper>
    </Widget>
  );
};

export default NotLoadedTyresWidget;
